import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { msalConfig } from '../auth/authConfig';
import useUserStore from '../store/userStore';
import useFetchSensors from './useFetchSensors';
import useFetchInventorySummary from './useFetchInventorySummary';
import { errorHandler } from '../utils/helpers';
import { acquireToken } from '../utils/auth';

export interface InventoryUploadResult {
  sensorId: string;
  isProcessed: boolean;
  message: string;
}

export interface InventoryUploadResponse {
  processed: number;
  failed: number;
  results: InventoryUploadResult[];
}

const useUploadInventory = () => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { user } = useUserStore();
  const { fetchSensors } = useFetchSensors();
  const { fetchInventorySummary } = useFetchInventorySummary();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadInventory = async (file: File): Promise<InventoryUploadResponse | null> => {
    if (!accounts.length || !user) {
      navigate('/sign-in');
      return null;
    }

    setLoading(true);
    try {
      const token = await acquireToken(instance, {
        scopes: [`${msalConfig.auth.clientId}/.default`],
        account: accounts[0],
      });
      const formData = new FormData();
      formData.append('inventoryFile', file);

      const response = await axios.post<InventoryUploadResponse>(
        `${window.__env.REACT_APP_API_URL || process.env.REACT_APP_API_URL}/Sensor/Upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json, text/plain, */*',
            userId: user.id,
          },
        },
      );

      await fetchSensors({ page: 1 });
      fetchInventorySummary();

      const uploadResponse = response.data;
      return {
        processed: uploadResponse?.processed ?? 0,
        failed: uploadResponse?.failed ?? 0,
        results: Array.isArray(uploadResponse?.results) ? uploadResponse.results : [],
      };
    } catch (uploadError) {
      errorHandler(uploadError, setError);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { uploadInventory, loading, error };
};

export default useUploadInventory;
